// Gera a "Simulação de pagamento no cartão" (extrato de parcelamento) como jsPDF doc,
// pronta pra converter em JPEG (jsPdfDocToJpegs) e mandar por WhatsApp — reusa o mesmo
// cano de envio de imagem do orçamento (ModalEnviarWhatsApp). NÃO recalcula nenhum valor:
// só formata o que já veio pronto do backend (parcelamento de cartão de crédito).
import jsPDF from 'jspdf';
import { formatMoeda } from '@fiodecobre/shared';

export interface ParcelaExtrato {
  n: number;
  total: number; // centavos
  valor_parcela: number; // centavos
  sem_juros: boolean;
}

export interface DadosExtratoParcelamento {
  numeroOrcamento: string; // ex: "005506"
  provedor: 'Mercado Pago' | 'PagBank';
  parcelas: ParcelaExtrato[];
  operador?: string; // nome do operador que gerou a simulação (carimbo na linha do provedor)
}

// Paleta do documento (cravada — é um documento com identidade própria, não o tema do app).
const COR_HEADER_DE = [124, 45, 18] as const; // #7c2d12
const COR_HEADER_ATE = [180, 83, 9] as const; // #b45309
const COR_TITULO_N = [124, 45, 18] as const; // #7c2d12
const COR_CINZA_DE = [120, 113, 108] as const; // #78716c
const COR_VALOR = [28, 25, 23] as const; // #1c1917
const COR_TOTAL = [87, 83, 78] as const; // #57534e
const COR_SEM_JUROS = [22, 101, 52] as const; // #166534
const COR_FUNDO_CLARO = [250, 250, 249] as const; // #fafaf9
const COR_LABEL_COLUNA = [168, 162, 158] as const; // #a8a29e
const COR_BORDA_CLARA = [240, 233, 226] as const; // #f0e9e2
const COR_RODAPE_BG = [28, 25, 23] as const; // #1c1917
const COR_RODAPE_SUB = [168, 162, 158] as const; // #a8a29e
const COR_RODAPE_MUTED = [120, 113, 108] as const; // #78716c

const LARGURA_PX = 640; // largura de referencia do mockup
const MM_POR_PX = 25.4 / 96; // assume ~96dpi de referencia do mockup
const LARGURA = LARGURA_PX * MM_POR_PX; // ~169.3mm

function px(n: number): number {
  return n * MM_POR_PX;
}

function carregarImagem(url: string): Promise<{ dataUrl: string; w: number; h: number } | null> {
  return fetch(url)
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('imagem nao encontrada'))))
    .then(
      (blob) =>
        new Promise<{ dataUrl: string; w: number; h: number } | null>((resolve) => {
          const fr = new FileReader();
          fr.onload = () => {
            const dataUrl = fr.result as string;
            const img = new Image();
            img.onload = () => resolve({ dataUrl, w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = dataUrl;
          };
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        }),
    )
    .catch(() => null);
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

// jsPDF nao tem gradiente nativo simples — desenha em faixas horizontais finas
// interpolando a cor, de cima pra baixo.
function desenharGradiente(doc: jsPDF, x: number, y: number, w: number, h: number, de: readonly number[], ate: readonly number[]) {
  const passos = Math.max(1, Math.ceil(h * 2)); // ~2 faixas/mm — suave o bastante
  const passoH = h / passos;
  for (let i = 0; i < passos; i++) {
    const t = passos === 1 ? 0 : i / (passos - 1);
    const r = lerp(de[0]!, ate[0]!, t);
    const g = lerp(de[1]!, ate[1]!, t);
    const b = lerp(de[2]!, ate[2]!, t);
    doc.setFillColor(r, g, b);
    doc.rect(x, y + i * passoH, w, passoH + 0.1, 'F'); // +0.1 evita frestas de arredondamento
  }
}

export default async function gerarExtratoParcelamento(dados: DadosExtratoParcelamento): Promise<jsPDF> {
  const { numeroOrcamento, provedor, parcelas, operador } = dados;

  // Carimbo do momento da simulação (relógio local = máquina do operador = Brasília).
  const agora = new Date();
  const carimboData = agora.toLocaleDateString('pt-BR');
  const carimboHora = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const qr = await carregarImagem('/qr-catalogo.png');

  // ===== Altura elastica: soma cada bloco do mockup =====
  const altHeader = px(78);
  const altColHeader = px(34);
  const altLinha = px(58);
  const altProvedor = px(40);
  const altRodape = px(112);
  const altTotal = altHeader + altColHeader + altLinha * parcelas.length + altProvedor + altRodape;

  const doc = new jsPDF({ unit: 'mm', format: [LARGURA, altTotal] });
  const w = doc.internal.pageSize.getWidth();
  let y = 0;

  // === 1) HEADER — gradiente cobre, título + subtítulo brancos ===
  desenharGradiente(doc, 0, y, w, altHeader, COR_HEADER_DE, COR_HEADER_ATE);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('FIODECOBRE', px(24), y + px(36));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11.5);
  doc.setTextColor(255, 255, 255);
  // "branco translucido" simulado com cinza claro (jsPDF nao tem alpha de texto simples e confiavel entre versoes)
  doc.setTextColor(240, 224, 214);
  doc.text(`Simulação de pagamento no cartão · Orçamento ${numeroOrcamento}`, px(24), y + px(58));
  y += altHeader;

  // === 2) CABECALHO DE COLUNAS ===
  doc.setFillColor(...COR_FUNDO_CLARO);
  doc.rect(0, y, w, altColHeader, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...COR_LABEL_COLUNA);
  doc.text('PARCELAS', px(24), y + altColHeader / 2 + px(3), { baseline: 'middle' });
  doc.text('TOTAL A PAGAR', w - px(24), y + altColHeader / 2 + px(3), { align: 'right', baseline: 'middle' });
  y += altColHeader;

  // === 3) LINHAS — 1 por parcela ===
  parcelas.forEach((p) => {
    doc.setDrawColor(...COR_BORDA_CLARA);
    doc.setLineWidth(0.2);
    doc.line(0, y, w, y);

    const centroY = y + altLinha / 2;

    // Esquerda: "Nx" + "de" + "R$ valor_parcela"
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15.5);
    doc.setTextColor(...COR_TITULO_N);
    const nxTxt = `${p.n}x`;
    doc.text(nxTxt, px(24), centroY + px(5), { baseline: 'middle' });
    const nxW = doc.getTextWidth(nxTxt);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12.5);
    doc.setTextColor(...COR_CINZA_DE);
    const deTxt = ' de ';
    doc.text(deTxt, px(24) + nxW, centroY + px(5), { baseline: 'middle' });
    const deW = doc.getTextWidth(deTxt);

    doc.setFont('helvetica', 'normal'); // peso 500 -> normal (jsPDF/helvetica so tem normal/bold)
    doc.setFontSize(18.5);
    doc.setTextColor(...COR_VALOR);
    doc.text(formatMoeda(p.valor_parcela / 100), px(24) + nxW + deW, centroY + px(5), { baseline: 'middle' });

    // Direita: "total R$ X" + selo "sem juros" no 1x
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11.5);
    doc.setTextColor(...COR_TOTAL);
    const totalTxt = `total ${formatMoeda(p.total / 100)}`;
    if (p.sem_juros) {
      doc.text(totalTxt, w - px(24), centroY - px(2), { align: 'right', baseline: 'middle' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COR_SEM_JUROS);
      doc.text('sem juros', w - px(24), centroY + px(14), { align: 'right', baseline: 'middle' });
    } else {
      doc.text(totalTxt, w - px(24), centroY, { align: 'right', baseline: 'middle' });
    }

    y += altLinha;
  });

  // === 4) LINHA DO PROVEDOR ===
  doc.setDrawColor(...COR_BORDA_CLARA);
  doc.line(0, y, w, y);
  doc.setFillColor(...COR_FUNDO_CLARO);
  doc.rect(0, y, w, altProvedor, 'F');
  const centroProvY = y + altProvedor / 2 + px(3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...COR_CINZA_DE);
  const prefixo = 'Simulação com as taxas do cartão ';
  doc.text(prefixo, px(24), centroProvY, { baseline: 'middle' });
  const prefixoW = doc.getTextWidth(prefixo);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COR_TOTAL);
  doc.text(provedor, px(24) + prefixoW, centroProvY, { baseline: 'middle' });

  // Carimbo à direita: data · hora · operador (o espaço vazio ao lado do provedor)
  const carimbo = [carimboData, carimboHora, operador?.trim()].filter(Boolean).join(' · ');
  if (carimbo) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COR_LABEL_COLUNA);
    doc.text(carimbo, w - px(24), centroProvY, { align: 'right', baseline: 'middle' });
  }
  y += altProvedor;

  // === 5) RODAPE escuro — QR + texto ===
  doc.setFillColor(...COR_RODAPE_BG);
  doc.rect(0, y, w, altRodape, 'F');

  const qrTam = px(78);
  const qrPad = px(8);
  const qrX = px(24);
  const qrY = y + (altRodape - qrTam - qrPad * 2) / 2;
  doc.setFillColor(255, 255, 255);
  doc.rect(qrX, qrY, qrTam + qrPad * 2, qrTam + qrPad * 2, 'F');
  if (qr) {
    doc.addImage(qr.dataUrl, 'PNG', qrX + qrPad, qrY + qrPad, qrTam, qrTam);
  }

  const textoX = qrX + qrTam + qrPad * 2 + px(20);
  let textoY = y + px(34);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Conheça nosso catálogo', textoX, textoY);

  textoY += px(20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...COR_RODAPE_SUB);
  doc.text('Elétrica · Ar · Piscina · Ferragens', textoX, textoY);

  const hoje = new Date().toLocaleDateString('pt-BR');
  textoY += px(20);
  doc.setFontSize(8.5);
  doc.setTextColor(...COR_RODAPE_MUTED);
  const larguraTexto = w - textoX - px(24);
  const linhaValidade = doc.splitTextToSize(
    `Simulação válida para ${hoje}. Parcelas sujeitas à bandeira do cartão.`,
    larguraTexto,
  ) as string[];
  doc.text(linhaValidade, textoX, textoY);

  return doc;
}
